"use client";

import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useLanguage } from '@/context/LanguageContext';

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i: number = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.65, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function CTASection() {
  const { t } = useLanguage();
  const sectionRef = useRef<HTMLDivElement>(null);
  const inView = useInView(sectionRef, { once: true, amount: 0.2 });

  return (
    <section id="contact" ref={sectionRef} className="py-28 bg-[#07111F] relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[rgba(255,145,46,0.3)] to-transparent" />
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[400px] pointer-events-none"
        style={{ background: 'radial-gradient(ellipse, rgba(255,145,46,0.08) 0%, transparent 70%)' }}
      />
      <div
        className="absolute top-0 left-[20%] w-[300px] h-[300px] blob pointer-events-none opacity-10"
        style={{ background: 'radial-gradient(circle, rgba(30,58,95,0.8) 0%, transparent 70%)' }}
      />
      <div className="max-w-container mx-auto px-6 text-center relative z-10">
        <motion.div variants={fadeUp} initial="hidden" animate={inView ? 'visible' : 'hidden'} custom={0}>
          <div className="section-label mb-5">{t('cta_label')}</div>
        </motion.div>

        <motion.h2
          variants={fadeUp}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          custom={1}
          className="font-heading font-bold text-white mb-6"
          style={{ fontSize: 'clamp(2.2rem, 5vw, 3.8rem)', lineHeight: '1.08', letterSpacing: '-0.03em' }}
        >
          {t('cta_headline_1')}{' '}
          <span className="text-gradient-orange">{t('cta_headline_accent')}</span>
          <br />
          {t('cta_headline_2')}
        </motion.h2>

        <motion.p
          variants={fadeUp}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          custom={2}
          className="text-muted text-lg max-w-xl mx-auto mb-10 leading-relaxed"
        >
          {t('cta_sub')}
        </motion.p>

        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          custom={3}
          className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12"
        >
          <a
            href="https://calendly.com/devros-free-consultation?utm_source=website"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary px-10 py-5 rounded-xl text-lg font-bold w-full sm:w-auto text-center inline-flex items-center justify-center gap-3"
          >
            <span>{t('cta_button')}</span>
            <svg width="18" height="18" fill="none" stroke="white" strokeWidth="2.5" viewBox="0 0 24 24">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </a>
        </motion.div>

        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          custom={4}
          className="flex flex-wrap items-center justify-center gap-8"
        >
          {[
            { icon: '🔒', text: t('cta_trust1') },
            { icon: '⏱️', text: t('cta_trust2') },
            { icon: '🎯', text: t('cta_trust3') },
          ].map((item) => (
            <div key={item.text} className="flex items-center gap-2 text-muted text-sm">
              <span>{item.icon}</span>
              <span>{item.text}</span>
            </div>
          ))}
        </motion.div>

        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          custom={5}
          className="mt-12 pt-8 border-t border-[rgba(139,158,192,0.1)] flex flex-col sm:flex-row items-center justify-center gap-8 text-sm text-muted"
        >
          <a href="mailto:contact@devros.ai" className="flex items-center gap-2 hover:text-primary transition-colors">
            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
              <polyline points="22,6 12,13 2,6"/>
            </svg>
            contact@devros.ai
          </a>
          <span className="hidden sm:block text-[rgba(139,158,192,0.3)]">·</span>
          <span className="flex items-center gap-2">
            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/>
            </svg>
            {t('cta_location')}
          </span>
        </motion.div>
      </div>
    </section>
  );
}